import { MoreInformation } from '@hex/calculators-shared/components';
import { getPaymentTable, PaymentTableItem } from '@hex/shared/utils';
import { Dialog, tokens, InteractiveTable, Typography, ModalLayout, Link, Pagination } from '@sapphire-react/core';
import { sumBy } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { LoanCalculatorForm } from '../../../types';
import { formatNumber } from '../../../utils';

type Props = {
  data?: LoanCalculatorForm;
};

export const MoreInformationCost = ({ data }: Props) => {
  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  if (!data) return null;

  const tableData: PaymentTableItem[] = getPaymentTable({
    loanAmount: data.loanAmount,
    interestRate: parseFloat(data.interest),
    repaymentMethod: data.repaymentMethod,
    maturity: data.maturity,
  });

  const columns = [
    { uid: 'installmentCount', name: t('moreInformation.loanPaymentPlan.modal.month') },
    { uid: 'currentPayment', name: t('moreInformation.loanPaymentPlan.modal.payment') },
    { uid: 'payment', name: t('moreInformation.loanPaymentPlan.modal.abbreviation') },
    { uid: 'interest', name: t('moreInformation.loanPaymentPlan.modal.interest') },
    { uid: 'loanAmount', name: t('moreInformation.loanPaymentPlan.modal.capital') },
  ];

  const startIndex = Math.max((page - 1) * pageSize, 0);
  const endIndex = Math.min(page * pageSize, tableData.length);

  return (
    <MoreInformation
      headerText={t('moreInformation.heading')}
      items={[
        {
          key: 'how-we-calculate-data',
          text: 'moreInformation.howTo.label',
          content: (
            <>
              <Typography.Body marginBottom={tokens.size.spacingMd}>
                {t('moreInformation.howTo.paragraph1')}
              </Typography.Body>
              <Typography.Body marginBottom={tokens.size.spacingMd}>
                {t('moreInformation.howTo.paragraph2')}
              </Typography.Body>
            </>
          ),
        },
        {
          key: 'loan-payment-plan',
          text: 'moreInformation.loanPaymentPlan.label',
          content: (
            <>
              <Typography.Body marginBottom={tokens.size.spacingMd}>
                {t('moreInformation.loanPaymentPlan.text', {
                  currentPayment: formatNumber(Math.round(tableData[0].currentPayment)),
                  length: tableData.length,
                  totalAmount: formatNumber(sumBy(tableData, 'currentPayment')),
                  interest: formatNumber(data.interest, true),
                })}
              </Typography.Body>
              <Link
                onPress={() => {
                  setIsDialogOpen(true);
                }}
              >
                {t('moreInformation.loanPaymentPlan.linkText')}
              </Link>

              <Dialog onClose={() => setIsDialogOpen(false)} isOpen={isDialogOpen} isDismissable>
                <ModalLayout
                  header={<ModalLayout.Header heading={t('moreInformation.loanPaymentPlan.modal.header')} />}
                  body={
                    <ModalLayout.Body>
                      <InteractiveTable
                        aria-label={t('moreInformation.loanPaymentPlan.modal.header')}
                        cellOverflow={'wrap'}
                        footer={
                          <Pagination
                            itemCount={tableData.length}
                            pageSize={pageSize}
                            page={page}
                            defaultPageSize={10}
                            pageSizeOptions={[5, 10]}
                            onChange={({ page: newPage, pageSize: newPageSize }) => {
                              setPage(newPage);
                              setPageSize(newPageSize);
                            }}
                          />
                        }
                      >
                        <InteractiveTable.Header columns={columns}>
                          {(column) => (
                            <InteractiveTable.Column
                              id={column.uid}
                              maxWidth={column.uid === 'installmentCount' ? 20 : undefined}
                            >
                              {column.name}
                            </InteractiveTable.Column>
                          )}
                        </InteractiveTable.Header>
                        <InteractiveTable.Body items={tableData.slice(startIndex, endIndex)}>
                          {(item) => (
                            <InteractiveTable.Row id={item.installmentCount} columns={columns}>
                              {({ uid }) => (
                                <InteractiveTable.Cell id={uid}>
                                  <NoWrap>
                                    {uid === 'installmentCount'
                                      ? item[uid as keyof typeof item]
                                      : `${formatNumber(Math.round(item[uid as keyof typeof item]))} €`}
                                  </NoWrap>
                                </InteractiveTable.Cell>
                              )}
                            </InteractiveTable.Row>
                          )}
                        </InteractiveTable.Body>
                      </InteractiveTable>
                    </ModalLayout.Body>
                  }
                />
              </Dialog>
            </>
          ),
        },
        {
          key: 'loan-security-payment',
          text: 'moreInformation.loanSecurityPayment.label',
          content: (
            <>
              <Typography.Body marginBottom={tokens.size.spacingMd}>
                {t('moreInformation.loanSecurityPayment.paragraph1')}
              </Typography.Body>
              <Typography.Body marginBottom={tokens.size.spacingMd}>
                {t('moreInformation.loanSecurityPayment.paragraph2')}
              </Typography.Body>
              <Typography.Body marginBottom={tokens.size.spacingMd}>
                {t('moreInformation.loanSecurityPayment.paragraph3')}
              </Typography.Body>
            </>
          ),
        },
      ]}
    />
  );
};

const NoWrap = styled.span`
  white-space: nowrap;
`;
